<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { cloneDeep, debounce } from 'lodash-es'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'

import noProject from '@/assets/images/ide/no-project.svg'
import manageCollaboratorIcon from '@/assets/images/ide/users-group.svg'

import { languagesItems, type ILanguage } from '@/utils/sharedData/languages'
import { LANGUAGEMETA, type IMeta } from '@/utils/meta'
import { IDEVIEWMODELS } from '@/utils/models'
import type { IUser } from '@/components/organisation/interface/IUser'
import projectsService, {
  type IProject,
  type IModelProject,
  type ILoadProjectsRequest,
  type ICollaborator,
  ProjectSortOrder,
  ProjectSortType
} from '@/services/ide/projects.service'
import utilModelsService from '@/services/util.models.service'
import settingService from '@/services/ide/settings/setting.service'
import liveCodingService from '@/services/ide/liveCoding.service'
import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'
import { useLiveCodeStore } from '@/stores/liveCode.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import { useProjectManager } from '@/stores/projectManager.store'
import ideModal from '@/components/ide/ide/IdeModal.vue'
import Button from '@/components/atoms/button/index.vue'
import SortIcon from '@/components/ide/ide/settings/SortIcon.vue'
import PaginationComp from '@/components/shared/PaginationComp.vue'

enum DROPDOWN_TYPE {
  LANGUAGE = 'language',
  USERS = 'users'
}

const authStore = useAuthStore()
const ideStore = useIdeStore()
const liveCodeStore = useLiveCodeStore()
const organisationStore = useOrganisationStore()
const projectManager = useProjectManager()
const route = useRoute()
const router = useRouter()

const isMounted = ref<boolean>(false)
const dropdown = ref<null | HTMLDivElement>(null)
const userDropdown = ref<null | HTMLDivElement>(null)

const allLanguages: ILanguage[] = cloneDeep(languagesItems)
const selectedLanguage = ref<ILanguage | null>(null)
const selectUsers = ref<IUser | null>(null)
const icon = ref<string | null>(null)
const filteredProjects = ref<IModelProject[]>([])
const selectedKeywords = ref<string | null>(null)
const searchProjectWithUser = ref<string>('')
const searchTerm = ref<string>('')
const showDropdown = ref<boolean>(false)
const showUsersDropdown = ref<boolean>(false)
const userList = ref<IUser[]>([])
const isLoading = ref<boolean>(true)
const isClearFilter = ref<boolean>(false)
const showFilterDateDropdown = ref<boolean>(false)
const activeSort = ref<{ type: ProjectSortType; order: ProjectSortOrder }>({
  type: ProjectSortType.DATE_MODIFIED_SORT,
  order: ProjectSortOrder.DESC
})

const languages: ILanguage[] = cloneDeep(languagesItems)
  .filter((lang: ILanguage) => !lang.isAdvanced)
  .sort((a: ILanguage, b: ILanguage) => (a.displayName > b.displayName ? 1 : -1))
const languageMeta: IMeta = cloneDeep(LANGUAGEMETA)

const openEditor = computed(() => {
  return projectManager.isSelectedProject && !ideStore.isAdvanced
    ? projectManager.isSelectedProject
    : ideStore.isSelectedProject
})

const isProjectsMounted = computed(() => {
  return isMounted.value
})
const projects = computed(() => {
  return ideStore.isProjects || []
})

const pageDetail = computed(() => {
  return ideStore.pageDetail
})

const isProjects = computed(() => {
  return filteredProjects.value.length > 0
})

const links = computed<ILanguage[]>(() => {
  if (searchTerm.value !== '') {
    return languages.filter((o: ILanguage) => {
      return o.searchTerm.includes(searchTerm.value.toLowerCase())
    })
  } else return languages
})

const searchUserList = computed(() => {
  return userList.value.filter((user: IUser) => {
    return (
      user.firstName.toLowerCase().includes(searchProjectWithUser.value.toLowerCase()) ||
      (user.lastName &&
        user.lastName.toLowerCase().includes(searchProjectWithUser.value.toLowerCase()))
    )
  })
})

const isOrganisationOwner = computed(() => {
  return organisationStore.isOrganisationOwner || organisationStore.isOrganisationAdmin
})

/**
 * Function to get the default sort order dynamically
 * @param sortType The type of sort to check
 * @returns The default sort order, or 'desc' if not active
 */
const getSortOrder = (sortType: ProjectSortType) =>
  computed(() => (activeSort.value.type === sortType ? activeSort.value.order : undefined))

const defaultProjectNameSortOrder = getSortOrder(ProjectSortType.PROJECT_NAME_SORT)
const defaultLanguageSortOrder = getSortOrder(ProjectSortType.LANGUAGE_SORT)
const defaultProjectOwnerSortOrder = getSortOrder(ProjectSortType.PROJECT_OWNER_SORT)
const defaultDateSortOrder = computed(() => {
  return isDateSort.value ? activeSort.value.order : undefined
})

const isDateSort = computed(() => {
  return (
    activeSort.value.type === ProjectSortType.DATE_MODIFIED_SORT ||
    activeSort.value.type === ProjectSortType.DATE_CREATED_SORT
  )
})

/**
 * show language drop down
 * @param type - show dropdown according to type
 */
const showDropDown = (type: string) => {
  if (type === DROPDOWN_TYPE.LANGUAGE) showDropdown.value = true
  if (type === DROPDOWN_TYPE.USERS) {
    showUsersDropdown.value = true
    searchProjectWithUser.value = ''
  }
}

/**
 * Load project
 * @param selecedProject IProject
 */
const loadProject = async (selecedProject: IProject) => {
  let language: string | null = null
  if (!selectedLanguage.value?.language) language = selecedProject?.language as string
  else language = selectedLanguage.value?.language as string

  ideStore.isDefaultIde &&
    ideStore.setIdeMeta(languageMeta[language as keyof typeof languageMeta] as IMeta)
  if (selecedProject?.isOwner) {
    await showCollaboratorList(selecedProject)
  }
  await projectsService.loadProject(selecedProject, language)
  projectManager.setLoadCollabModal(false)
}

/**
 *
 * @param event - event
 */
const handleOutsideClick = (event: any) => {
  if (dropdown.value && !dropdown.value.contains(event.target)) showDropdown.value = false

  if (userDropdown.value && !userDropdown.value.contains(event.target)) {
    showUsersDropdown.value = false
    if (selectUsers.value) {
      searchProjectWithUser.value = `${selectUsers.value.firstName} ${
        selectUsers.value.lastName ? selectUsers.value.lastName : ''
      }`
    }
  }
}

/**
 * Handle the click of the language
 * @param value gives the selected value
 * @param type - identify the type of dropdown
 */
const handleClick = (value: ILanguage | IUser, type: string) => {
  if (type === DROPDOWN_TYPE.LANGUAGE) {
    const language = value as ILanguage
    showDropdown.value = false
    selectedLanguage.value = language
    searchTerm.value = language.displayName
  } else if (type === DROPDOWN_TYPE.USERS) {
    const user = value as IUser
    showUsersDropdown.value = false
    selectUsers.value = user
    searchProjectWithUser.value = `${user.firstName} ${user.lastName ? user.lastName : ''}`
  }
  filterProjects(false)
}

/**
 * Open project
 * @param selecedProject IProject
 */
const openProject = async (selecedProject: IProject | null = null) => {
  // If limit reached of exceeded prevent editing projects
  if (authStore.isCreateProjectLimitExceeded) {
    if (selecedProject) {
      selecedProject.readOnly = true
    }
  }

  projectManager.setComingFromProjectManager(true)
  if (liveCodeStore.isLiveCodingActive) liveCodingService.disconnectLiveCoding()
  let selectedData = selectedLanguage.value
  if (!selectedLanguage.value)
    selectedData =
      languages.find((lang: ILanguage) => lang?.language === selecedProject?.language) || null

  if (selectedData) {
    let link = ''
    if (selecedProject?.multipleFile) {
      link = allLanguages.find((langauge: ILanguage) => {
        return langauge.language == selectedData?.language && langauge.isAdvanced
      })?.link as string
    } else {
      link = selectedData?.link as string
    }
    if (
      selectedData.language !== route.meta.langauge &&
      selecedProject?.multipleFile === ideStore.isAdvanced
    ) {
      projectManager.setIsAwaitsOpenFlag(true)
      ideStore.setCodeUpdated(false)
    } else projectManager.setIsAwaitsOpenFlag(true)
    ideStore.setCodeUpdated(false)
    router.push(link)
  }
  if (selecedProject) {
    await loadProject(selecedProject)
  }
  projectManager.setComingFromProjectManager(true)
  settingService.closePopup(IDEVIEWMODELS.MYPROJECTS)
  await new Promise((resolve) => setTimeout(resolve, 500))
  openProjectDefaultIde()
  await projectsService.postProjectLoad()
  projectManager.setProjectPermissionWithId({
    id: selecedProject?.id,
    readOnly: selecedProject?.readOnly,
    isOwner: selecedProject?.isOwner,
    language: selecedProject?.language
  })
}

/**
 * @description - To load the save ide before injecting ace editor
 * @param count - the count
 * @returns null
 */
const openProjectDefaultIde = async (count: number = 0) => {
  if (!useIdeStore().isWindowAce() || ideStore.isDefaultIde) {
    if (count > 10) {
      return null
    } else {
      await new Promise((resolve) => setTimeout(resolve, 600))
      openProjectDefaultIde(count + 1)
    }
  } else {
    projectsService.openProject(openEditor.value, true)
  }
}

/**
 * Delete project
 * @param selecedProject IProject
 */
const deleteProject = async (selecedProject: IProject | null = null) => {
  projectManager.setDeleteProjectInProgress(true)
  const projectId = ideStore?.isProjectId
  await utilModelsService
    .confirmPromise(
      'Are you sure, do you want to delete this project?',
      null,
      IDEVIEWMODELS.MYPROJECTS
    )
    .then(async () => {
      let isLastPageLastItem = false
      if (
        pageDetail.value &&
        pageDetail.value.currentPage !== 0 &&
        projects.value.length === 1 &&
        pageDetail.value.currentPage + 1 === pageDetail.value.totalPages
      ) {
        isLastPageLastItem = true
      }

      await projectsService.deleteProject(selecedProject as IProject).then(async () => {
        projectsService.deleteExistingProjectInSession(projectId)
        if (selecedProject?.id === projectId) {
          projectManager.setGeneratedProjectName('')
          router.push({ name: 'start-coding' })
        } else {
          if (isLastPageLastItem) {
            if (pageDetail.value) {
              ideStore.setCurrentPage(pageDetail.value.currentPage - 1)
            }
          }
          filterProjects(false, true)
        }
      })
    })
    .finally(() => {
      projectManager.setDeleteProjectInProgress(false)
    })
}
/**
 * @param clearFilterOption - make the API call with clearing the language param
 * clear all filters
 */
const clearFilters = (clearFilterOption: boolean = false) => {
  selectedKeywords.value = null
  filteredProjects.value = projects.value
  isMounted.value = true
  searchTerm.value = ''
  showDropdown.value = false
  showUsersDropdown.value = false
  icon.value = null
  selectedLanguage.value = null
  searchProjectWithUser.value = ''
  isClearFilter.value = true
  selectUsers.value = null
  clearFilterOption && filterProjects(false)
  activeSort.value = {
    type: ProjectSortType.DATE_MODIFIED_SORT,
    order: ProjectSortOrder.DESC
  }
}

/**
 * Function for calling the API for showing all projects
 * @param clearSelectedLanguage - clear the language param
 * @param pageChanged - flag to determine this fn is running on page change or not
 */
const filterProjects = async (
  clearSelectedLanguage: boolean = true,
  pageChanged: boolean = false
) => {
  await projectsService.postInitProjectLimit()

  const loadProjectsRequest: ILoadProjectsRequest = {
    language:
      selectedLanguage.value?.language || !clearSelectedLanguage
        ? selectedLanguage.value?.language
        : (route.meta.language as string),
    isMultiFile: ideStore.isAdvanced || null,
    name: selectedKeywords.value,
    email: selectUsers.value?.email,
    pageIndex: pageChanged ? pageDetail.value?.currentPage || 0 : 0,
    pageSize: 10,
    [activeSort.value.type]: activeSort.value.order
  }

  isLoading.value = true
  if (!pageChanged) {
    await getAllUsers()
  }
  await projectsService.loadProjectsForProjectManager(loadProjectsRequest)
  isLoading.value = false
  filteredProjects.value = projects.value
  isClearFilter.value = false
}

/**
 * @param listIcon - list of languages
 * @returns - finding the icon for the language and return the svg icon
 */
const showLanguageIcon = (listIcon: string) => {
  const filterIcon = languages.find((icon) => icon.language == listIcon)
  if (ideStore?.colorMode === 'dark')
    return filterIcon?.darkIcon ? filterIcon.darkIcon : filterIcon?.icon
  else return filterIcon?.icon
}

const searchWithDebounce = debounce(() => {
  filterProjects(true)
}, 500)

/**
 * @description - gets all the users for the respective organization
 */
const getAllUsers = async () => {
  await projectsService
    .collaboratorList()
    .then((response) => {
      userList.value = response.data
    })
    .catch((error) => error)
}

/**
 * @description - Add toggle between the modal
 */
const closeModalToggleFunc = () => {
  if (projectManager.deleteProjectInProgress) return

  if (router.options.history.state.back === '/dashboard') {
    router.push('/dashboard')
  } else {
    if (ideStore.isDefaultIde) projectsService.toggledModal()
  }
}

/**
 * @description - open the project
 * @param project - get the project details
 */
const openCollaborator = (project: IModelProject) => {
  if (isOrganisationOwner.value || project.isOwner) {
    loadProject(project)
    projectManager.setComingFromProjectManager(true)
    projectManager.setLoadCollabModal(true)
    if (project?.collaborators.length !== 0) settingService.openPopup(IDEVIEWMODELS.PROJECTSHARE)
  }
}

/**
 * @param project - gets the selected projectId for collab list
 * @description - makes an API call to get the list of collaborator
 */
const showCollaboratorList = async (project: IProject) => {
  await projectsService.listOfCollaborator(project)
}

/**
 * @param sort project name
 */
const handleSortProjectName = (sort: ProjectSortOrder) => {
  activeSort.value.type = ProjectSortType.PROJECT_NAME_SORT
  activeSort.value.order = sort
  filterProjects(false)
}

/**
 * @param sort language
 */
const handleSortLanguage = (sort: ProjectSortOrder) => {
  activeSort.value.type = ProjectSortType.LANGUAGE_SORT
  activeSort.value.order = sort
  filterProjects(false)
}
/**
 * @param sort project owner
 */
const handleSortProjectOwner = (sort: ProjectSortOrder) => {
  activeSort.value.type = ProjectSortType.PROJECT_OWNER_SORT
  activeSort.value.order = sort
  filterProjects(false)
}

/**
 *  Date created
 */
const handleSortDateCreated = () => {
  activeSort.value.type = ProjectSortType.DATE_CREATED_SORT
  activeSort.value.order = ProjectSortOrder.DESC
  filterProjects(false)
  showFilterDateDropdown.value = false
}

/**
 *  Date modified
 */
const handleSortDateModified = () => {
  activeSort.value.type = ProjectSortType.DATE_MODIFIED_SORT
  activeSort.value.order = ProjectSortOrder.DESC
  filterProjects(false)
  showFilterDateDropdown.value = false
}

/**
 * @param sort Date
 */
const handleSortDate = (sort: ProjectSortOrder) => {
  activeSort.value.order = sort
  filterProjects(false)
}

/**
 * @param collaborators and filters general access collaborators that is not shared using
 * @returns filtered collaborators
 */
const filterGAUsers = (collaborators: ICollaborator[]): ICollaborator[] => {
  return collaborators?.filter((clb) => !clb.sharedInGeneralAccess)
}

/**
 * Trigger on next or previous click
 * @param page new page number
 */
function pageChange(page: any) {
  ideStore.setCurrentPage(page - 1)
  filterProjects(false, true)
}

/**
 * Clears user filter and resets related fields
 */
const handleClearUserFilter = () => {
  showUsersDropdown.value = true
  selectUsers.value = null
  searchProjectWithUser.value = ''
  filterProjects(false)
}

/**
 * Check the JS in the language and remove it
 * @param inputText - takes the language
 * @returns - removing the js
 */
const removeJsFromLanguage = (inputText: string) => {
  const searchLanguage = languages.find((lang: ILanguage) => lang?.language === inputText)
  if (searchLanguage?.advanceCompiler) return inputText.replace(/js\b/gi, '')
  else return inputText
}

onMounted(() => {
  if (!isProjects.value) filterProjects()
  clearFilters() //Clear filters when switching from mobile/desktop
  selectedLanguage.value = null
  document.addEventListener('click', handleOutsideClick)

  watch(projects, () => {
    filteredProjects.value = projects.value
  })

  const language = languages.find((o: ILanguage) => {
    return o.language === route.meta.language
  })
  if (language) {
    searchTerm.value = language.displayName
    selectedLanguage.value = language
  }
  if (filteredProjects.value) return (filteredProjects.value = [])
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
})

watch(
  () => [selectedLanguage.value, showDropdown.value],
  () => {
    if (
      selectedLanguage.value?.darkIcon &&
      ideStore.colorMode === 'dark' &&
      searchTerm.value.length !== 0
    )
      icon.value = selectedLanguage.value?.darkIcon as string
    else if (selectedLanguage.value?.icon && searchTerm.value.length !== 0)
      icon.value = selectedLanguage.value?.icon
  }
)

watch(
  () => searchTerm.value,
  () => {
    if (searchTerm.value.length === 0) {
      icon.value = null
      selectedLanguage.value = null
      !isClearFilter.value && filterProjects(false)
    }
  }
)

// Expose the clearFilters function
defineExpose({
  clearFilters
})
</script>

<template>
  <ideModal
    title="Project manager"
    class="my-auto"
    :isProjectModal="true"
    :closeFunction="closeModalToggleFunc"
  >
    <div class="flex h-full min-w-full gap-2 sm:h-[80vh] lg:min-w-[92vw]">
      <div v-if="isProjectsMounted" class="h-full w-full space-y-4">
        <div
          class="border-primary flex w-full flex-col-reverse flex-wrap items-center gap-3 border-b pb-2 sm:flex-col xl:flex-row"
        >
          <div class="flex w-full flex-wrap gap-3 md:flex-nowrap">
            <div
              ref="dropdown"
              class="flex w-full grow flex-col flex-wrap gap-3 lg:items-start xl:flex-nowrap"
            >
              <label class="flex items-center text-sm font-medium">Pick a language:</label>
              <div class="relative flex grow lg:w-full">
                <img
                  v-if="icon && icon.length > 0"
                  :src="icon"
                  alt="language logo"
                  class="absolute left-2 top-1/2 mr-2 h-8 w-8 -translate-y-1/2 p-1"
                />
                <input
                  autocomplete="off"
                  v-model="searchTerm"
                  class="background-primary stroke-primary-default dark:focus-text-white text-md block h-10 w-full rounded-md border font-medium outline-none"
                  :class="[
                    { 'px-5': !icon || icon.length == 0 },
                    { 'px-12': icon && icon.length > 0 }
                  ]"
                  @focus="showDropDown(DROPDOWN_TYPE.LANGUAGE)"
                />
                <FontAwesomeIcon
                  icon="fa-magnifying-glass"
                  class="absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-2/4"
                />

                <div
                  v-if="showDropdown"
                  class="background-primary stroke-primary-default absolute z-50 mt-10 w-full rounded-md border p-3"
                >
                  <div class="max-h-60 overflow-y-auto md:grid">
                    <button
                      v-for="item in links"
                      :key="item.displayName"
                      class="w-full"
                      @click="handleClick(item, DROPDOWN_TYPE.LANGUAGE)"
                    >
                      <div
                        class="hover:background-secondary hover:text-high text-mid flex items-center justify-start rounded-lg px-2 py-3"
                      >
                        <img
                          :src="showLanguageIcon(item.language as string)"
                          :alt="item.displayName"
                          class="me-2 w-8"
                        />
                        <span class="inline-block text-sm">{{ item.displayName }}</span>
                        <!-- Empty message -->
                      </div>
                    </button>
                    <p v-if="links.length == 0" class="text-quinary w-full text-sm">
                      No results found
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              ref="userDropdown"
              class="flex w-full grow flex-col flex-wrap gap-3 lg:items-start xl:flex-nowrap"
            >
              <label class="flex items-center text-sm font-medium">Select a user:</label>
              <div class="relative flex grow lg:w-full">
                <input
                  autocomplete="off"
                  class="background-primary stroke-primary-default dark:focus-text-white text-md block h-10 w-full rounded-md border pl-3 font-medium outline-none"
                  v-model="searchProjectWithUser"
                  @focus="showDropDown(DROPDOWN_TYPE.USERS)"
                />
                <FontAwesomeIcon
                  v-if="!selectUsers"
                  icon="fa-magnifying-glass"
                  class="absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-2/4"
                />
                <FontAwesomeIcon
                  v-else
                  icon="fa-close"
                  class="absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-2/4 cursor-pointer"
                  @click="handleClearUserFilter"
                />

                <div
                  v-if="showUsersDropdown"
                  class="background-primary stroke-primary-default absolute z-50 mt-10 w-full rounded-md border p-3"
                >
                  <div class="max-h-60 overflow-y-auto md:grid">
                    <button
                      v-for="(item, idx) in searchUserList"
                      :key="idx"
                      class="w-full"
                      @click="handleClick(item, DROPDOWN_TYPE.USERS)"
                    >
                      <div
                        class="hover:background-secondary hover:text-high text-mid flex items-center justify-start rounded-lg px-2 py-3"
                      >
                        <span class="inline-block text-sm"
                          >{{ item.firstName }} {{ item.lastName }}</span
                        >
                      </div>
                    </button>
                    <p v-if="searchUserList.length == 0" class="text-quinary w-full text-sm">
                      No results found
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex w-full grow flex-col flex-wrap gap-3 lg:items-start xl:flex-nowrap">
              <p class="flex items-center text-sm font-medium">Search projects:</p>
              <div class="relative flex grow lg:w-full">
                <input
                  class="background-primary stroke-primary-default dark:focus-text-white text-md input-secondary block h-10 w-full rounded-md border p-1 pl-3 text-sm font-medium outline-none"
                  type="text"
                  v-model="selectedKeywords"
                  @input="searchWithDebounce"
                />
                <FontAwesomeIcon
                  icon="fa-magnifying-glass"
                  class="absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-2/4"
                />
              </div>
            </div>
            <div class="hidden w-fit grow items-end justify-center gap-3 md:flex">
              <Button
                color="secondary"
                variant="link"
                size="small"
                class="h-10 w-max px-1 py-1.5"
                @click="clearFilters(true)"
              >
                Clear all filters
              </Button>
            </div>
          </div>
          <div class="flex w-full items-center justify-between gap-3 xl:flex-nowrap">
            <div class="flex text-slate-500 hover:text-slate-700 sm:hidden">
              <FontAwesomeIcon class="px-2" icon="fa-folder-plus" />
              <span
                class="cursor-pointer text-sm font-medium underline"
                @click="projectsService.toggledModal"
                >Create a new Project
              </span>
            </div>
            <button
              class="flex h-10 w-max items-center rounded px-1 py-1.5 text-sm font-normal leading-none text-slate-500 underline hover:text-slate-600 md:hidden"
              @click="clearFilters(true)"
            >
              Clear all filters
            </button>
          </div>
        </div>
        <div v-if="!isProjects" class="flex h-full flex-col items-center justify-center lg:h-2/3">
          <img :src="noProject" alt="No Projects" class="h-40 w-40" v-if="!isLoading" />
          <div class="text-base text-new-secondary-500" v-if="!isLoading">
            No projects available.
            <FontAwesomeIcon class="h-4 w-4 px-2" icon="fa-folder-plus" />
            <span
              class="cursor-pointer text-base font-medium text-new-secondary-500 underline hover:text-slate-500"
              @click="projectsService.toggledModal"
              >Create a new Project
            </span>
          </div>
        </div>
        <div v-if="isLoading" class="flex h-full flex-col items-center justify-center">
          Loading ....
        </div>

        <div
          class="mt-0 hidden h-full auto-rows-min overflow-auto lg:grid"
          v-if="!isLoading && isProjects"
        >
          <div
            class="text-mid surface-primary-base sticky top-0 grid grid-cols-7 gap-6 px-4 pb-3 text-xs font-semibold sm:text-sm md:grid-cols-8"
          >
            <div class="col-span-2 flex items-center gap-2">
              <p>Project Name</p>
              <SortIcon
                class="shrink-0"
                :default="defaultProjectNameSortOrder"
                @onClick="handleSortProjectName"
              />
            </div>
            <div class="col-span-1 flex items-center gap-2">
              <p>Language</p>
              <SortIcon
                class="shrink-0"
                :default="defaultLanguageSortOrder"
                @onClick="handleSortLanguage"
              />
            </div>
            <div class="col-span-1 flex items-center gap-2">
              <p>Project Owner</p>
              <SortIcon
                class="shrink-0"
                :default="defaultProjectOwnerSortOrder"
                @onClick="handleSortProjectOwner"
              />
            </div>

            <div class="col-span-1 flex items-center gap-2">
              <p>My Permissions</p>
            </div>

            <div class="col-span-1 flex items-center gap-2">
              <p>Collaborators</p>
            </div>

            <div class="col-span-1 flex items-center gap-2">
              <p
                :class="isDateSort ? 'cursor-pointer' : 'pointer-events-none'"
                @click="showFilterDateDropdown = !showFilterDateDropdown"
              >
                Date
                {{ activeSort.type === ProjectSortType.DATE_MODIFIED_SORT ? 'Modified on' : '' }}
                {{ activeSort.type === ProjectSortType.DATE_CREATED_SORT ? 'Created on' : '' }}
              </p>
              <SortIcon
                v-if="isDateSort"
                class="shrink-0"
                :default="defaultDateSortOrder"
                @onClick="handleSortDate"
              />
              <FontAwesomeIcon
                v-else
                icon="fa-angle-down"
                class="h-3 w-3 cursor-pointer"
                @click="showFilterDateDropdown = !showFilterDateDropdown"
              />
              <div
                v-if="showFilterDateDropdown"
                class="section-new-secondary-50 absolute z-50 mt-28 w-fit rounded-md border border-[#B4C7EB] p-1"
              >
                <div class="max-h-60 overflow-y-auto md:grid">
                  <div
                    class="hover:section-new-secondary-100 flex cursor-pointer items-center justify-start rounded-lg px-4 py-2"
                    @click="handleSortDateCreated"
                  >
                    <span class="text-sm font-normal text-new-secondary-500">
                      Date created on
                      <FontAwesomeIcon
                        v-if="activeSort.type === ProjectSortType.DATE_CREATED_SORT"
                        icon="fa-check"
                        class="h-3 w-3 pl-2"
                      />
                    </span>
                  </div>
                  <div
                    class="hover:section-new-secondary-100 flex cursor-pointer items-center justify-start rounded-lg px-4 py-2"
                    @click="handleSortDateModified"
                  >
                    <span class="text-sm font-normal text-new-secondary-500">
                      Date modified on
                      <FontAwesomeIcon
                        v-if="activeSort.type === ProjectSortType.DATE_MODIFIED_SORT"
                        icon="fa-check"
                        class="h-3 w-3 pl-2"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mb-40 h-full w-full overflow-y-auto">
            <div
              v-for="project in filteredProjects"
              :key="project.id"
              class="background-primary stroke-primary-default text-primary group mb-2 grid grid-cols-8 items-center gap-2 self-start rounded-md p-2 text-xs sm:text-sm"
            >
              <div class="col-span-2 flex items-center">
                <img
                  :src="showLanguageIcon(project.language as string)"
                  alt=""
                  class="mr-2 h-6 w-6"
                />
                <p class="truncate text-sm font-medium">
                  {{ project.name }}
                </p>
                <img
                  v-if="filterGAUsers(project?.collaborators)?.length"
                  :src="manageCollaboratorIcon"
                  alt=""
                  class="ml-2 h-5 w-5"
                />
              </div>
              <p class="text-mid col-span-1 pl-2 capitalize">
                {{ removeJsFromLanguage(project.language) }}
              </p>
              <p class="text-mid col-span-1 truncate pl-2">
                {{ project.ownerName }}
              </p>
              <p class="col-span-1 pl-2">
                <span
                  class="background-secondary col-span-2 flex w-fit items-center justify-between gap-1 rounded-2xl px-2 py-1 text-new-secondary-600 outline-none"
                >
                  {{ project.readOnly ? 'Viewer' : 'Editor' }}
                </span>
              </p>
              <div class="col-span-1 pl-2">
                <div
                  class="flex items-center space-x-[-10px]"
                  :class="
                    isOrganisationOwner || project.isOwner ? 'cursor-pointer' : 'cursor-not-allowed'
                  "
                  @click="openCollaborator(project)"
                >
                  <div
                    v-for="collab in filterGAUsers(project?.collaborators)?.slice(0, 3)"
                    :key="collab.email"
                    class="background-primary stroke-primary-default text-high flex h-7 w-7 items-center justify-center rounded-full border text-sm font-medium"
                  >
                    {{ collab.name.at(0)?.toUpperCase() }}
                  </div>
                  <div
                    v-if="filterGAUsers(project?.collaborators)?.length > 3"
                    class="background-primary stroke-primary-default text-high flex h-7 w-7 items-center justify-center rounded-full border text-sm font-medium"
                  >
                    +{{ filterGAUsers(project?.collaborators)?.length - 3 }}
                  </div>
                </div>
              </div>
              <div class="text-mid col-span-1 flex flex-wrap pl-2">
                <p class="pr-2">
                  {{
                    moment(
                      activeSort.type === ProjectSortType.DATE_MODIFIED_SORT
                        ? project.updatedAt
                        : project.createdAt
                    ).format('ll')
                  }}
                </p>
                <p class="text-low">
                  {{
                    moment(
                      activeSort.type === ProjectSortType.DATE_MODIFIED_SORT
                        ? project.updatedAt
                        : project.createdAt
                    ).format('LT')
                  }}
                </p>
              </div>
              <div
                class="col-span-1 flex w-full items-center justify-center gap-4 text-new-secondary-400 group-hover:text-new-secondary-700"
              >
                <button @click="openProject(project)" data-testid="OpenProject">
                  <FontAwesomeIcon
                    icon="fa-arrow-up-right-from-square"
                    class="h-4 w-4"
                    data-testid="OpenProjectbtn"
                  />
                </button>
                <button
                  :class="project.readOnly && 'cursor-not-allowed'"
                  :disabled="project.readOnly"
                  @click="deleteProject(project)"
                >
                  <FontAwesomeIcon icon="fa-trash-can" class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!isLoading && isProjects"
          class="flex h-full w-full flex-col gap-3 overflow-y-auto pb-40"
        >
          <div
            v-for="project in filteredProjects"
            :key="project.id"
            class="background-primary stroke-primary-default text-primary group rounded-md border p-3"
            @click="openProject(project)"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex flex-col items-start justify-start">
                <div class="flex w-full items-end justify-start">
                  <img
                    :src="showLanguageIcon(project.language as string)"
                    alt=""
                    class="mr-2 h-6 w-6"
                  />
                  <div class="flex w-56 items-center justify-start sm:w-full">
                    <p class="truncate text-lg font-medium" :title="project.name">
                      {{ project.name }}
                    </p>
                    <img
                      v-if="filterGAUsers(project?.collaborators)?.length"
                      :src="manageCollaboratorIcon"
                      alt=""
                      class="ml-2 h-5 w-5"
                    />
                  </div>
                </div>
                <div class="text-mid ml-8 text-sm font-normal">
                  {{ moment(project?.createdAt).format('lll') }}
                </div>
              </div>
              <button
                :disabled="project.readOnly"
                class="pt-0.5 text-new-secondary-400 group-hover:text-new-secondary-700"
                :class="project.readOnly && 'cursor-not-allowed'"
                @click.stop="deleteProject(project)"
              >
                <FontAwesomeIcon icon="fa-trash-can" class="h-4 w-4" />
              </button>
            </div>
            <div class="ml-8 flex items-start justify-between">
              <div class="w-1/2 truncate text-xs">
                Owner:
                <span class="text-mid truncate text-sm font-light"> {{ project?.ownerName }}</span>
              </div>
              <div class="flex w-1/2 items-center justify-end gap-1 text-xs">
                Permission:
                <span
                  class="background-secondary flex w-fit gap-1 rounded-2xl px-2 py-1 text-new-secondary-600 outline-none"
                >
                  {{ project.readOnly ? 'Viewer' : 'Editor' }}
                </span>
              </div>
            </div>
            <div
              class="text-mid ml-8 flex w-fit items-center space-x-[-10px]"
              :class="
                isOrganisationOwner || project.isOwner ? 'cursor-pointer' : 'cursor-not-allowed'
              "
              @click.stop="openCollaborator(project)"
            >
              <div
                v-for="collab in filterGAUsers(project?.collaborators)?.slice(0, 3)"
                :key="collab.email"
                class="background-primary stroke-primary-default text-high flex h-7 w-7 items-center justify-center rounded-full border text-sm font-medium"
              >
                {{ collab.name.at(0)?.toUpperCase() }}
              </div>
              <div
                v-if="filterGAUsers(project?.collaborators)?.length > 3"
                class="background-primary stroke-primary-default text-high flex h-7 w-7 items-center justify-center rounded-full border text-sm font-medium"
              >
                +{{ filterGAUsers(project?.collaborators)?.length - 3 }}
              </div>
            </div>
          </div>
        </div>
        <div class="surface-primary-base sticky bottom-0 flex w-full items-end justify-center">
          <PaginationComp
            :current="pageDetail ? pageDetail.currentPage + 1 : 0"
            :total="pageDetail ? pageDetail.totalPages : 0"
            :newStyle="true"
            @pageChange="pageChange"
          />
        </div>
      </div>
    </div>
  </ideModal>
</template>
